/**
 * aideySessionsService.js
 * Persists Aidey chat sessions to localStorage (runtime) and DB (durable).
 * Each session: { id, title, messages[], createdAt, updatedAt }
 */
import { api } from '../api/client';
import { trimKeepingSettings } from '../utils/chatAreas';   // C-b: a long chat keeps its toggles

const KEY = 'aidey_sessions';
const MAX_SESSIONS = 200;
const MAX_MESSAGES_PER_SESSION = 150;

export function loadSessions() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
}

export function loadSession(id) {
  if (!id) return null;
  return loadSessions().find(s => s.id === id) || null;
}

export function saveSession(session) {
  const all = loadSessions();
  const idx = all.findIndex(s => s.id === session.id);
  const updated = { ...session, updatedAt: new Date().toISOString() };
  if (idx >= 0) all[idx] = updated;
  else all.unshift(updated);
  localStorage.setItem(KEY, JSON.stringify(all.slice(0, MAX_SESSIONS)));
  window.dispatchEvent(new Event('aidey:onboardingProgress'));
  return updated;
}

// Keep a copy of a chat read from the server exactly as it is (title, dates, messages), so that
// opening it changes nothing: it is not renamed, re-dated or moved to the top of the history.
export function cacheSession(session) {
  if (!session?.id) return;
  const all = loadSessions().filter(s => s.id !== session.id);
  all.unshift({ ...session, messages: trimKeepingSettings(session.messages || [], MAX_MESSAGES_PER_SESSION) });
  localStorage.setItem(KEY, JSON.stringify(all.slice(0, MAX_SESSIONS)));
}

export function deleteSession(id) {
  const remaining = loadSessions().filter(s => s.id !== id);
  localStorage.setItem(KEY, JSON.stringify(remaining));
}

export function deleteAllSessions() {
  localStorage.removeItem(KEY);
}

/** Generate a stable unique ID for a new session. */
export function newSessionId() {
  return `aidey_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Persist chat messages into a session (localStorage only).
 * Only saves if there's at least one user message (ignores welcome-only sessions).
 * Auto-derives title from the first user message.
 */
// Returns true only when it saved something new, so callers refresh only after a real change
// (the chat page re-fetched the whole list on every render-time call: about once a second live).
export function persistMessages(sessionId, messages) {
  if (!sessionId) return false;
  const userMsg = messages.find(m => m.role === 'user');
  if (!userMsg) return false; // nothing worth saving

  const existing = loadSession(sessionId);
  const last = (list) => (list && list.length ? `${list.length}:${list[list.length - 1].ts || ''}:${String(list[list.length - 1].content || '').length}` : '');
  const kept = trimKeepingSettings(messages, MAX_MESSAGES_PER_SESSION);
  if (existing && last(existing.messages) === last(kept)) return false;
  const title = userMsg.content.slice(0, 60) + (userMsg.content.length > 60 ? '…' : '');

  saveSession({
    id: sessionId,
    title: existing?.title || title,
    createdAt: existing?.createdAt || new Date().toISOString(),
    messages: kept,
  });
  return true;
}

// ── DB-backed operations ───────────────────────────────────────────────────

/** Generate a short AI title for a session via the backend. */
export async function generateSessionTitle(firstMessage) {
  try {
    const res = await api('/api/aidey-sessions/title', {
      method: 'POST',
      body: JSON.stringify({ firstMessage }),
    });
    if (!res.ok) throw new Error();
    const data = await res.json();
    return data.title || firstMessage.slice(0, 50);
  } catch {
    return firstMessage.slice(0, 50);
  }
}

/** Save/update a session to the database. Fire-and-forget safe. */
export async function saveSessionToDb(session) {
  if (!session?.id) return;
  try {
    await api('/api/aidey-sessions/save', {
      method: 'POST',
      body: JSON.stringify({ id: session.id, title: session.title, messages: trimKeepingSettings(session.messages, MAX_MESSAGES_PER_SESSION), projectId: session.projectId }),
    });
  } catch { /* non-fatal */ }
}

/** Load the most recent session from DB (within last 24h). Returns null if none. */
export async function getLastDbSession() {
  try {
    const res = await api('/api/aidey-sessions/last');
    if (!res.ok) return null;
    const data = await res.json();
    return data.session || null;
  } catch { return null; }
}

/** Load full session (including messages) from DB by id. */
export async function loadSessionFromDb(id) {
  try {
    const res = await api(`/api/aidey-sessions/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.session || null;
  } catch { return null; }
}

/** Fetch all sessions from DB (metadata only). Returns [] on error. */
// THROWS when the list could not be read. It returned [] on any failure, so a 401 drew "Start a
// conversation and it will appear here" over a server full of chats (2026-10-08, live). A failed
// load is not an empty list; the caller shows "Couldn't load your chats" with Retry.
export async function loadSessionsFromDb() {
  const res = await api('/api/aidey-sessions/');   // api() refreshes the token and retries once
  const data = await res.json();
  if (!Array.isArray(data?.sessions)) throw new Error('The chat list came back unreadable.');
  return data.sessions;
}

/** Delete a session from DB. */
export async function deleteSessionFromDb(id) {
  try {
    await api(`/api/aidey-sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch { /* non-fatal */ }
}

/** Rename a session in DB. */
export async function renameSessionInDb(id, title) {
  try {
    await api(`/api/aidey-sessions/${encodeURIComponent(id)}/title`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  } catch { /* non-fatal */ }
}

/** Clear all sessions from DB. */
export async function deleteAllSessionsFromDb() {
  try {
    await api('/api/aidey-sessions/', { method: 'DELETE' });
  } catch { /* non-fatal */ }
}
